/**
 * FieldVisibilityRuleDialog - create/edit a field visibility rule.
 *
 * Embeds <ConditionBuilder> and adds target_field, effect, severity, message.
 */

import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Switch } from '@/shared/components/ui/switch'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { ConditionBuilder } from '@/admin/components/condition-builder'
import { useAvailableRefs } from '@/admin/components/condition-builder/use-available-refs'
import type { PredicateNode } from '@/admin/components/condition-builder/types'
import {
  useCreateFieldVisibilityRule,
  useUpdateFieldVisibilityRule,
  type FieldVisibilityRule,
  type FieldVisibilityEffect,
} from '@/admin/hooks/use-field-visibility-rules'
import type { Severity } from '@/admin/hooks/use-validation-rules'
import { useRegistrationFields } from '@/admin/hooks/use-registration-fields'

import { toastApiError } from '@/shared/lib/api-error'
const EFFECTS: { value: FieldVisibilityEffect; label: string }[] = [
  { value: 'show', label: 'Show field' },
  { value: 'hide', label: 'Hide field' },
  { value: 'require', label: 'Make required' },
  { value: 'optional', label: 'Make optional' },
]

const SEVERITIES: { value: Severity; label: string }[] = [
  { value: 'BLOCK', label: 'Block' },
  { value: 'WARN', label: 'Warn' },
  { value: 'INFO', label: 'Info' },
]

interface Props {
  open: boolean
  onOpenChange: (next: boolean) => void
  rule: FieldVisibilityRule | null
}

interface FormState {
  name: string
  description: string
  conditions: PredicateNode | null
  severity: Severity
  message: string
  target_field: string
  effect: FieldVisibilityEffect
  is_active: boolean
}

const EMPTY: FormState = {
  name: '',
  description: '',
  conditions: null,
  severity: 'INFO',
  message: '',
  target_field: '',
  effect: 'show',
  is_active: true,
}

export function FieldVisibilityRuleDialog({ open, onOpenChange, rule }: Props) {
  const { refs, byRef, isLoading: refsLoading } = useAvailableRefs()
  const { data: fields } = useRegistrationFields()
  const create = useCreateFieldVisibilityRule()
  const update = useUpdateFieldVisibilityRule()

  const [form, setForm] = useState<FormState>(EMPTY)
  const [seedKey, setSeedKey] = useState<string>('')
  const nextKey = open ? (rule?.uuid ?? 'new') : '__closed__'
  if (nextKey !== seedKey) {
    setSeedKey(nextKey)
    if (!open) {
      setForm(EMPTY)
    } else if (rule) {
      setForm({
        name: rule.name,
        description: rule.description ?? '',
        conditions: rule.conditions,
        severity: rule.severity,
        message: rule.message,
        target_field: rule.target_field,
        effect: rule.effect,
        is_active: rule.is_active,
      })
    } else {
      setForm(EMPTY)
    }
  }

  const busy = create.isPending || update.isPending

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  async function handleSave() {
    if (!form.name.trim()) {
      toast.error('Rule name is required')
      return
    }
    if (!form.target_field) {
      toast.error('Select a target field')
      return
    }
    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      conditions: form.conditions,
      severity: form.severity,
      message: form.message.trim(),
      target_field: form.target_field,
      effect: form.effect,
      is_active: form.is_active,
    }
    try {
      if (rule) {
        await update.mutateAsync({ uuid: rule.uuid, ...payload })
        toast.success('Rule updated')
      } else {
        await create.mutateAsync(payload)
        toast.success('Rule created')
      }
      onOpenChange(false)
    } catch (err) {
      toastApiError(err, 'Failed to save rule');
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[640px] sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle>
            {rule ? 'Edit field visibility rule' : 'New field visibility rule'}
          </DialogTitle>
          <DialogDescription>
            Control when a registration field is shown, hidden, or required
            based on conditions.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label
              htmlFor="fvr-name"
              className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]"
            >
              Name
            </Label>
            <Input
              id="fvr-name"
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
              placeholder="e.g. Hide installer when structure is apartment"
              className="h-9 text-[12px]"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                Target field
              </Label>
              <Select
                value={form.target_field || '__none__'}
                onValueChange={(v) =>
                  setField('target_field', v === '__none__' ? '' : v)
                }
              >
                <SelectTrigger className="h-9 text-[12px]">
                  <SelectValue placeholder="Select field…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__" disabled>
                    Select field…
                  </SelectItem>
                  {(fields ?? []).map((f) => (
                    <SelectItem key={f.uuid} value={f.uuid}>
                      {f.display_name} ({f.code_name})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                Effect
              </Label>
              <Select
                value={form.effect}
                onValueChange={(v) =>
                  setField('effect', v as FieldVisibilityEffect)
                }
              >
                <SelectTrigger className="h-9 text-[12px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EFFECTS.map((e) => (
                    <SelectItem key={e.value} value={e.value}>
                      {e.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
              Conditions
            </Label>
            {refsLoading ? (
              <div className="rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3 py-4 text-center text-[12px] text-[var(--ink-3)]">
                Loading fields…
              </div>
            ) : (
              <ConditionBuilder
                value={form.conditions}
                onChange={(next) => setField('conditions', next)}
                availableRefs={refs}
                byRef={byRef}
              />
            )}
          </div>

          <div className="grid grid-cols-[200px_1fr] gap-3">
            <div className="flex flex-col gap-1.5">
              <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                Severity
              </Label>
              <Select
                value={form.severity}
                onValueChange={(v) => setField('severity', v as Severity)}
              >
                <SelectTrigger className="h-9 text-[12px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SEVERITIES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label
                htmlFor="fvr-message"
                className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]"
              >
                Message
              </Label>
              <Input
                id="fvr-message"
                value={form.message}
                onChange={(e) => setField('message', e.target.value)}
                placeholder="Optional message when the rule fires"
                className="h-9 text-[12px]"
              />
            </div>
          </div>

          <Button
            variant="ghost"
            type="button"
            onClick={() => setField('is_active', !form.is_active)}
            className="h-auto w-full justify-start gap-3 whitespace-normal rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3 py-2 text-left hover:border-[var(--green-border)]"
          >
            <Switch checked={form.is_active} decorative />
            <div>
              <span className="block text-[12px] text-[var(--ink)]">
                {form.is_active ? 'Active' : 'Inactive'}
              </span>
              <span className="mt-0.5 block text-[11px] text-[var(--ink-3)]">
                Inactive rules are retained but never evaluated.
              </span>
            </div>
          </Button>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button
            variant="outline"
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={busy}
            className="bg-[var(--green)] text-white hover:bg-[var(--green-hover)]"
          >
            {busy ? 'Saving…' : rule ? 'Save changes' : 'Create rule'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
